<template>
    <div :class="[
        props.inline 
            ? 'relative flex items-center' 
            : 'fixed left-1/2 transform -translate-x-1/2 w-fit flex flex-row items-center justify-center z-50 md:z-40'
    ]"
    :style="!props.inline && !isLivePerformance ? { bottom: 'max(1rem, calc(var(--sab, env(safe-area-inset-bottom, 0px)) + 1rem))' } : {}">
        <div class="size-7 sm:size-8 bg-slate-700 dark:bg-gray-800 rounded-full flex items-center justify-center cursor-pointer float-menu shadow-md border border-slate-600 dark:border-gray-700 hover:scale-105 active:scale-95 transition-all duration-150" 
            :class="{'hidden': isLivePerformance}"
            @click="toggleOptions()">
                <Tooltip :text="isExpanded ? 'Hide options' : 'Show options'" :position="'top'">
                    <Transition name="fade" mode="out-in">
                        <ChevronUpIcon 
                            v-if="!isExpanded"
                            class="size-4 text-white dark:!text-white hover:text-white dark:hover:text-white transition-transform duration-300" />
                        <XMarkIcon 
                            v-else
                            class="size-4 text-white dark:!text-white hover:text-white dark:hover:text-white transition-transform duration-300" />
                    </Transition>
                </Tooltip>
        </div>
        <Teleport to="body" :disabled="!isLivePerformance">
            <Transition name="fade" mode="out-in">
                <div v-if="isExpanded" 
                    class="flex flex-row gap-1 bg-slate-700 dark:bg-gray-800 rounded-md p-1 h-fit float-menu shadow-xl items-center"
                    :class="isLivePerformance 
                        ? 'fixed right-10 z-[150]' 
                        : 'absolute bottom-10 left-1/2 transform -translate-x-1/2 z-[90]'"
                    :style="isLivePerformance ? { top: 'max(0.5rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))' } : {}">
                    <button 
                        @click="toggleLivePerformance()"
                        class="px-2 py-1 text-xs rounded transition-all duration-200 float-menu-button"
                        :class="{
                            'float-menu-button-active': isLivePerformance && inControl, 
                            'text-gray-300 hover:text-white hover:bg-slate-600 dark:hover:bg-gray-700 cursor-not-allowed': !inControl
                        }"
                        :disabled="!inControl"
                    >
                        <PlayIcon v-if="!isLivePerformance" class="size-6 text-white dark:!text-white hover:text-white dark:hover:text-white transition-all" />
                        <StopIcon v-else class="size-6 text-white dark:!text-white hover:text-white dark:hover:text-white transition-all" />
                    </button>
                    <Divider class="rotate-90" />
                    <TimeCounter ref="timeCounter" @reset="onResetTimer" :disabled="!inControl" />
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, nextTick, watch } from 'vue';
import { XMarkIcon, ChevronUpIcon, PlayIcon, StopIcon } from '@heroicons/vue/24/outline';
import Divider from '@/components/common/TriadDivider.vue';
import TimeCounter from '@/components/common/TimeCounter.vue';
import appStore from '@/store/app.store.js';


const isExpanded = ref(false);
const isLivePerformance = ref(false);
const inControl = ref(true);
const emits = defineEmits(['livePerformance']);

const timeCounter = ref(null);

const props = defineProps({
    playlistId: {
        type: String,
        required: true
    },
    startCounter: {
        type: Boolean,
        required: false,
        default: false
    },
    inline: {
        type: Boolean,
        default: false
    }
});

const toggleOptions = () => {
    isExpanded.value = !isExpanded.value;
};

const toggleLivePerformance = () => {
    const nextState = !isLivePerformance.value;
    if (!nextState && timeCounter.value) {
        timeCounter.value.stopTimer();
    } else if (nextState && props.startCounter && timeCounter.value) {
        timeCounter.value.startTimer();
    }
    isLivePerformance.value = nextState;
    if (!nextState) {
        isExpanded.value = false;
    }
    emits('livePerformance', nextState);
};

const onResetTimer = () => {
    if (timeCounter.value) {
        timeCounter.value.stopTimer();
    }
    isLivePerformance.value = false;
    isExpanded.value = false;
    emits('livePerformance', false);
};

appStore.watch(state => state.livePerformance, (newVal) => {
    if (!newVal) {
        if (timeCounter.value) {
            timeCounter.value.stopTimer();
            timeCounter.value.resetTimer();
        }
        isLivePerformance.value = false;
        isExpanded.value = false;
        return;
    }
    isExpanded.value = true;
    isLivePerformance.value = true;
    setTimeout(() => {
        if (appStore.state.livePerformance) {
            isExpanded.value = true;
            isLivePerformance.value = true;
            nextTick(() => {    
                if (timeCounter.value) {
                    timeCounter.value.startTimer();
                }
            });
        }
    }, 1000);
});
appStore.watch(state => state.livePerformanceInControl, (newVal) => {
    inControl.value = newVal;
});

watch(() => props.startCounter, (newVal) => {
    if (!timeCounter.value) return;
    if (newVal) {
        if (isLivePerformance.value) {
            timeCounter.value.stopTimer();
        } else {
            timeCounter.value.startTimer();
        }
    } else if (!isLivePerformance.value && !appStore.state.livePerformance) {
        timeCounter.value.stopTimer();
        timeCounter.value.resetTimer();
    }
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>